/**
 * EmptyState — nothing matched (STORY-003-005; rebuilt 2026-08).
 *
 * WHAT WAS WRONG. A centred icon disc, a headline under it, a paragraph, and a
 * boxed callout — the exact assistant-template empty state this direction
 * refuses. It also read as a failure ("PackageSearch, Ничего не найдено") when
 * the truthful reading is usually "your filters excluded everything", which is
 * a thing the user can undo in one tap.
 *
 * WHAT IT IS NOW. A slip with nothing written on it. Left-aligned like every
 * other row on the page, headed in the display face, ruled off from the out-of-catalogue
 * offer below it — and this is the one surface where the shyrdak motif gets
 * REAL presence (0.14, not the 0.06 hero whisper), because an empty region is
 * precisely where an ornament belongs and precisely where this product's own
 * world should still be visible. No icon disc, no 2×2 grid, no kicker.
 *
 * The OOC block is a genuine placeholder: the submission flow is STORY-010-001,
 * so the CTA is `disabled` + `aria-disabled` rather than a link to nowhere. It
 * says what will be possible, not what is.
 *
 * Motion: the canonical small first-paint stagger — the slip rises, then its
 * blocks follow at 45ms. Gated on prefers-reduced-motion via `useGardenMotion`.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { MotifSurface } from "@/components/motion";
import { containerVariants, itemVariants } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

export interface EmptyStateProps {
  /**
   * Which nothing this is. `browse` blames the filters (the only thing the
   * user changed); `search` blames the WORD, because a search page with no
   * facets applied telling you to "change the filters" is advice about
   * controls that are all still at their defaults.
   */
  variant?: "browse" | "search";
}

export function EmptyState({ variant = "browse" }: EmptyStateProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const { reduced } = useGardenMotion();

  return (
    <MotifSurface
      tone="primary"
      opacity={0.14}
      className="rounded-[var(--g-radius)] border border-[color:var(--g-rule)] bg-card text-card-foreground"
    >
      {/* role="status" + the load-bearing testid stay on this node; the shared
          <Stagger> wrapper forwards only className/data-testid, so a raw
          motion.div keeps the live-region role here. */}
      <motion.div
        role="status"
        data-testid="browse-empty-state"
        className="g-ruled flex flex-col"
        variants={reduced ? undefined : containerVariants}
        initial={reduced ? false : "hidden"}
        animate={reduced ? undefined : "show"}
      >
        {/* Block 1 — what happened, and what to do about it. */}
        <motion.div
          className="flex flex-col gap-2 p-5 sm:p-6"
          variants={reduced ? undefined : itemVariants}
        >
          <h2 className="font-display text-lg font-bold leading-tight tracking-tight sm:text-xl">
            {t("empty.title")}
          </h2>
          <p className="max-w-prose text-sm leading-relaxed text-[color:var(--g-ink-2)]">
            {variant === "search"
              ? t("empty.searchDescription")
              : t("empty.description")}
          </p>
        </motion.div>

        {/* Block 2 — the out-of-catalogue offer. Placeholder until STORY-010-001
            wires the real submission; a disabled CTA is honest, a live-looking
            one that does nothing is not. */}
        <motion.div
          data-testid="browse-ooc-hint"
          className="flex flex-col items-start gap-2 bg-[color:var(--g-sunken)] p-5 sm:p-6"
          variants={reduced ? undefined : itemVariants}
        >
          <p className="text-sm font-semibold text-foreground">
            {t("empty.oocTitle")}
          </p>
          <p className="max-w-prose text-sm leading-relaxed text-[color:var(--g-ink-2)]">
            {t("empty.oocHint")}
          </p>
          <button
            type="button"
            disabled
            data-testid="browse-ooc-cta"
            aria-disabled="true"
            className="mt-1 inline-flex min-h-[44px] cursor-not-allowed items-center rounded-[10px] border border-[color:var(--g-rule-strong)] px-4 text-sm font-semibold text-[color:var(--g-ink-3)]"
          >
            {t("empty.oocCta")}
          </button>
        </motion.div>
      </motion.div>
    </MotifSurface>
  );
}
